const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function toDate(value: string): Date {
  const trimmed = value.trim()
  const isUtcIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z$/.test(trimmed)
  if (!isUtcIso) {
    return new Date(Number.NaN)
  }

  return new Date(trimmed)
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate())
}

/**
 * Compact relative label for dense table cells.
 *
 * Examples: 'just now', '25m', '3h', 'Yesterday', '4d', '12 Mar', '2024 12 Mar'
 */
export function formatShortRelativeDateTime(
  value: string,
  options?: { now?: Date },
): string {
  // Tolerate PocketBase's space-separated UTC timestamps alongside strict ISO.
  const date = toDate(value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value

  const now = options?.now ?? new Date()
  const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60_000)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m`

  const dayDiff = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) /
      (24 * 60 * 60 * 1000),
  )
  if (dayDiff === 0) return `${Math.floor(diffMinutes / 60)}h`
  if (dayDiff === 1) return 'Yesterday'
  if (dayDiff > 1 && dayDiff < 7) return `${dayDiff}d`

  const label = `${date.getDate()} ${MONTHS_SHORT[date.getMonth()] ?? ''}`
  if (date.getFullYear() === now.getFullYear()) return label
  return `${date.getFullYear()} ${label}`
}

function formatTime(value: Date): string {
  const hours24 = value.getHours()
  const minutes = value.getMinutes().toString().padStart(2, '0')
  const suffix = hours24 >= 12 ? 'pm' : 'am'
  const hours12 = hours24 % 12 || 12
  return `${hours12}:${minutes}${suffix}`
}

function dayWithOrdinal(day: number): string {
  const mod10 = day % 10
  const mod100 = day % 100
  if (mod10 === 1 && mod100 !== 11) return `${day}st`
  if (mod10 === 2 && mod100 !== 12) return `${day}nd`
  if (mod10 === 3 && mod100 !== 13) return `${day}rd`
  return `${day}th`
}

/**
 * Examples:
 * - Yesterday 5:15pm
 * - Mar, Sat 15th 8:03am
 * - 2024 Feb, Mon 12th 9:55am
 *
 * Set includeTime=false to hide the time segment.
 */
export function formatRelativeDateTime(
  value: string,
  options?: { now?: Date; includeTime?: boolean },
): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return value

  const now = options?.now ?? new Date()
  const includeTime = options?.includeTime ?? true
  const today = startOfDay(now)
  const target = startOfDay(date)
  const dayDiff = Math.round((today.getTime() - target.getTime()) / (24 * 60 * 60 * 1000))

  if (dayDiff === 0) {
    return includeTime ? `Today ${formatTime(date)}` : 'Today'
  }

  if (dayDiff === 1) {
    return includeTime ? `Yesterday ${formatTime(date)}` : 'Yesterday'
  }

  const month = MONTHS_SHORT[date.getMonth()] ?? ''
  const weekday = WEEKDAYS_SHORT[date.getDay()] ?? ''
  const day = dayWithOrdinal(date.getDate())
  const time = formatTime(date)

  if (date.getFullYear() === now.getFullYear()) {
    return includeTime
      ? `${month}, ${weekday} ${day} ${time}`
      : `${month}, ${weekday} ${day}`
  }

  return includeTime
    ? `${date.getFullYear()} ${month}, ${weekday} ${day} ${time}`
    : `${date.getFullYear()} ${month}, ${weekday} ${day}`
}

/**
 * The label for when a status change actually occurred — but only when that
 * reads differently from when it was recorded. A change logged in the moment
 * carries no occurredAt at all, and a back-date of a few minutes renders the
 * same label as the recorded time, so surfacing either would be noise.
 *
 * Returns null when there is nothing worth saying.
 */
export function differingOccurredLabel(
  occurredAt: string | undefined,
  createdAt: string,
  options?: { now?: Date },
): string | null {
  if (!occurredAt) return null
  const occurredLabel = formatRelativeDateTime(occurredAt, options)
  const recordedLabel = formatRelativeDateTime(createdAt, options)
  return occurredLabel === recordedLabel ? null : occurredLabel
}
